import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Info as InfoIcon,
  DirectionsRun as DirectionsRunIcon,
  SignalCellularAlt as SignalCellularAltIcon
} from '@mui/icons-material';
import { 
  Container,
  AppBar,
  Tabs,
  Tab,
  Paper,
  Grid
} from '@mui/material';
import TabPanel from '../components/TabPanel';
import MRTable from './../components/MRTable';
import { TABS } from '../helpers/Tabs';
import { urlsByStore } from '../.lyft-config';
import StoreContext from '../context/StoreContext'
import CourtesyRidesPopup from '../components/CourtesyRidesPopup';
import SimplePopup from '../components/SimplePopup';
import { useBebackApi } from '../hooks/useBebackApi';
import ComingSoonPopup from '../components/ComingSoonPopup';
import InformationPopup from '../components/InformationPopup';
import FeedbackContext from '../context/FeedbackContext';
import { MRTableModalsProvider } from '../context/MRTableModalsContext';

const SAFETY_CHECK_URL = process.env.REACT_APP_SAFETYCHECK_URL;

const BeBack = ({ currentWorkOrders }) => {
  const { getIsStoreValidSC } = useBebackApi()
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchedValue, setSearchedValue] = useState('');
  const [openCourtesyRidesPopup, setOpenCourtesyRidesPopup] = useState(false);
  const [lyftUrl, setLyftUrl] = useState('');
  const [openNoLyftAlert, setOpenNoLyftAlert] = useState(false);
  const [openNoSCPopup, setOpenNoSCPopup] = useState(false);
  const { storeId } = useContext(StoreContext);
  const [safetyCheckUrl, setSafetyCheckUrl] = useState(undefined);
  const [infoPopup, setInfoPopup] = useState('');
  const [openInfoPopup, setOpenInfoPopup] = useState(false);
  const safetyRef = useRef(null)
  const { setIsLoading } = useContext(FeedbackContext);

  useEffect(() => {
    const url = urlsByStore.filter((object) => object['Store Number'] === +storeId)
    setLyftUrl(url.length > 0 && url[0]['Lyft url'])
    
  }, [storeId]);

  useEffect(() => {
    if(safetyCheckUrl){
      safetyRef.current.click();
    }
  }, [safetyCheckUrl]);

  const handleGoToLyft = () => {
    if(lyftUrl){
      window.open(lyftUrl, "_blank");
      setOpenCourtesyRidesPopup(false);
    } else {
      setOpenNoLyftAlert(true);
    }
  }

  const handleGoToSafetyCheck = async () => {
    setIsLoading(true);
    const { isAllowed } = await getIsStoreValidSC(storeId);
    setIsLoading(false);
    if(!isAllowed){
      setSafetyCheckUrl(undefined);
      setOpenNoSCPopup(true);
    } else {
      setSafetyCheckUrl(
        `https://${SAFETY_CHECK_URL}/home/?currentStoreId=${storeId}`
      );
    }
  }

  return (
    <>
      {/* Title Container */}
      <Grid
        container
        direction="row"
        gap={"1em"}
        sx={{ alignItems: "flex-end" }}
      >
        <div className='message-runner-title-text'>
          Message Runner
          <span style={{ marginLeft: "7px" }}>
            <SignalCellularAltIcon className='message-runner-icon-bar' />
            <DirectionsRunIcon className='message-runner-icon-runner' />
          </span>
        </div>

        <button
          className="button red small"
          onClick={() => setOpenCourtesyRidesPopup(true)}
        >
          Courtesy Rides
        </button>
        <div className='message-runner-icon-div'>
          <InfoIcon 
            className='message-runner-icon-info'
            onClick={() => {setInfoPopup('courtesyRides'); setOpenInfoPopup(true)}}
          />
        </div>

        <button className="button red small" onClick={handleGoToSafetyCheck}>
          <a ref={safetyRef} href={safetyCheckUrl} target='safety-check' referrerPolicy='unsafe-url' style={{color: 'inherit', textDecoration: 'inherit'}}>Safety Check</a>
        </button>
      </Grid>

      <Container sx={{ marginY: "2em" }}>
        <AppBar position="static" elevation={0} color="default">
          <Tabs
            value={selectedTab}
            sx={{ background: "#DFE1E4" }}
            onChange={(event, newValue) => setSelectedTab(newValue)}
            indicatorColor="secondary"
            textColor="inherit"
            variant="fullWidth"
          >
            {TABS.map((tab, index) => (
              <Tab key={index} className='message-runner-tab' label={tab.title} />
            ))}
          </Tabs>
        </AppBar>
        <Paper
          sx={{
            border: "1px solid #b9b9b9",
            borderTop: "none",
            borderRadius: "0px",
          }}
        >
          {TABS.map((tab, index) => (
            <TabPanel value={selectedTab} index={index} key={index}>
              <MRTableModalsProvider>
                <MRTable
                  index={selectedTab}
                  currentWorkOrders={currentWorkOrders}
                  day={tab.day}
                  setIndex={setSelectedTab}
                  setSearchedValue={setSearchedValue}
                  searchedValue={searchedValue}
                  setInfoPopup={setInfoPopup}
                  setOpenInfoPopup={setOpenInfoPopup}
                />
              </MRTableModalsProvider>
            </TabPanel>
          ))}
        </Paper>
      </Container>

      {openCourtesyRidesPopup && (
        <CourtesyRidesPopup
          open={openCourtesyRidesPopup}
          onClose={() => setOpenCourtesyRidesPopup(false)}
          handleGoToLyft={handleGoToLyft}
        />
      )}

      {
        openNoLyftAlert && (
          <SimplePopup
            open={openNoLyftAlert}
            onClose={() => setOpenNoLyftAlert(false)}
            contentText={`It looks like your store is not setup to use Lyft. If you'd like to enroll your store into Lyft, please call the Help Desk at (800) 248-8854.`}
            actions={<button className='button red small' onClick={() => {setOpenCourtesyRidesPopup(false); setOpenNoLyftAlert(false)}}>Return to Message Runner</button>}
          />
        )
      }

      {
        openNoSCPopup && ( 
          <ComingSoonPopup
            open={openNoSCPopup}
            onClose={() => setOpenNoSCPopup(false)}
          />
        )
      }

      {
        openInfoPopup && (
          <InformationPopup 
            open={openInfoPopup}
            content={infoPopup} 
            onClose={() => setOpenInfoPopup(false)}
          />
        )
      }
    </>
  );
};

BeBack.propTypes = {
  currentWorkOrders: PropTypes.array,
};

export default BeBack;
